import { DATE_FORMAT } from '@/helpers/dates';
import { getOrgNameFromAlias } from '@/helpers/getOrgNameFromAlias';
import { useGetOrganizationPaymentPlanQuery } from '@/services/organizations';
import { useGetPaymentPlansQuery } from '@/services/paymentPlans';
import { Box, Button, DateInput, FormikRadioGroup } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers, useFormikContext, validateYupSchema, yupToFormErrors } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { object, Schema, string } from 'yup';

import { ProgramType } from 'types';

import { ScrollablePaymentPlanTable } from '../../../Payouts/ScrollablePaymentPlanTable';

import { PaymentPlanChangeImpactedAccounts } from './PaymentPlanChangeImpactedAccounts';

export interface PaymentFormEditData {
  shouldHavePaymentSchedule: string;
  paymentPlanId?: string | undefined;
  legalEntity: string;
  setUpBankAccount: string;
  netSuiteVendorId?: string | undefined;
  effectiveDate?: string | undefined;
  planToApplyToExistingAccounts?: string | undefined;
}

const paymentPlanChangeWorkflowSchema = () => ({
  shouldHavePaymentSchedule: string().required().oneOf(['yes', 'no']),
  paymentPlanId: string().when('shouldHavePaymentSchedule', {
    is: 'yes',
    then: (schema) => schema.required('A payment plan is required'),
    otherwise: (schema) => schema.optional(),
  }),
  legalEntity: string().required().oneOf(['yes', 'no']),
  planToApplyToExistingAccounts: string().when('shouldHavePaymentSchedule', {
    is: 'yes',
    then: () =>
      string().test({
        message: 'This field is required',
        test: function (value: any) {
          const { totalImpactedAccounts } = this.options.context || {};
          return totalImpactedAccounts > 0 ? Boolean(value) : true;
        },
      }),
    otherwise: () => string().optional(),
  }),
  effectiveDate: string().when('shouldHavePaymentSchedule', {
    is: 'yes',
    then: () =>
      string().test({
        message: 'This field is required',
        test: function (value: any) {
          const { currentPaymentPlanId } = this.options.context || {};
          return currentPaymentPlanId ? Boolean(value) : true;
        },
      }),
    otherwise: (schema) => schema.optional(),
  }),
  setUpBankAccount: string().required().oneOf(['yes', 'no']),
  netSuiteVendorId: string().optional(),
});

export const PaymentPlanChangeWorkflowSchema = (): Schema<PaymentFormEditData> =>
  object().shape(paymentPlanChangeWorkflowSchema()).noUnknown(true);

type SubmitHandler = (values: any, formikHelpers: FormikHelpers<any>) => void | Promise<any>;

const shouldHavePaymentScheduleOptions = [
  {
    id: 'shouldHavePaymentScheduleTrue',
    value: 'yes',
    label: 'Yes',
  },
  {
    id: 'shouldHavePaymentScheduleFalse',
    value: 'no',
    label: 'No, payments plan should be inherited from parent organization.',
  },
];

const legalEntityOptions = [
  {
    id: 'legalEntityTrue',
    value: 'yes',
    label: 'Yes',
  },
  {
    id: 'legalEntityFalse',
    value: 'no',
    label: 'No',
  },
];

const setUpBankAccountOptions = [
  {
    id: 'setUpBankAccountTrue',
    value: 'yes',
    label: `Yes`,
  },
  {
    id: 'setUpBankAccountFalse',
    value: 'no',
    label: 'No, payments should be made to parent organization',
  },
];

const InheritScheduleInput = ({
  organizationName,
  isSubmitting,
}: {
  organizationName: string;
  isSubmitting: boolean;
}) => {
  const { setFieldValue, values }: any = useFormikContext();
  useEffect(() => {
    if (values.shouldHavePaymentSchedule === 'no') {
      setFieldValue('paymentPlanId', undefined);
    }
  }, [setFieldValue, values.shouldHavePaymentSchedule]);

  return (
    <Field
      title={`Should ${organizationName} have its own payment plan?`}
      name="shouldHavePaymentSchedule"
      id="shouldHavePaymentSchedule"
      options={shouldHavePaymentScheduleOptions}
      component={FormikRadioGroup}
      isRequired
      isDisabled={isSubmitting}
    />
  );
};

interface PaymentFormProps {
  currentPaymentPlanId?: string;
  initialValues: any;
  handleSubmit: SubmitHandler;
  isLoading: boolean;
  disabledFields?: string[];
  organizationAlias: string;
  programType: ProgramType;
}

export const PaymentForm = ({
  currentPaymentPlanId,
  initialValues,
  handleSubmit,
  isLoading,
  organizationAlias,
  programType,
}: PaymentFormProps) => {
  const navigate = useNavigate();
  const [totalImpactedAccounts, setTotalImpactedAccounts] = useState(0);
  const { data: paymentPlan, isLoading: isLoadingPaymentPlan } = useGetOrganizationPaymentPlanQuery({
    alias: organizationAlias,
    programType,
  });
  const { data: paymentPlans, isLoading: isLoadingPaymentPlans } = useGetPaymentPlansQuery({
    programType,
  });
  const isLoadingData = isLoading || isLoadingPaymentPlan || isLoadingPaymentPlans;

  const onCancel = useCallback(() => {
    navigate(`/settings/organizations/${organizationAlias}/payments`);
  }, [navigate, organizationAlias]);

  const hideLegalSection = true; // This is a temporary flag to hide the legal section as per FIN-3197 requirement.

  return (
    <>
      <Formik
        validate={(values) => {
          try {
            validateYupSchema(values, PaymentPlanChangeWorkflowSchema(), true, {
              totalImpactedAccounts,
              currentPaymentPlanId,
            });
          } catch (error) {
            return yupToFormErrors(error);
          }
          return {};
        }}
        initialValues={initialValues}
        validateOnChange={false}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ isSubmitting, values, errors, touched, setFieldValue, validateForm }) => (
          <Form noValidate>
            <Box childGap="xl">
              <Box childGap="xl">
                <Box>
                  <InheritScheduleInput isSubmitting={isSubmitting} organizationName={organizationAlias} />
                </Box>
                {values.shouldHavePaymentSchedule === 'yes' && (
                  <Box padding="0 lg 0 0" childGap="xs">
                    <Box direction="row" alignItems="center" justifyContent="space-between">
                      {paymentPlan?.inherited && (
                        <>
                          <Box fontWeight="medium">Inherited From</Box>
                          <Box direction="row" childGap="xs" alignItems="center">
                            <Link
                              style={{ color: 'green' }}
                              to={`/settings/organizations/${paymentPlan.scheduleOwnerAlias}/payments`}
                            >
                              {paymentPlan.scheduleOwnerAlias && getOrgNameFromAlias(paymentPlan.scheduleOwnerAlias)}
                            </Link>
                          </Box>
                        </>
                      )}
                    </Box>
                    {paymentPlans && (
                      <Box childGap="xl">
                        <Box>
                          <ScrollablePaymentPlanTable
                            paymentPlans={paymentPlans}
                            programType={programType}
                            currentPaymentPlanId={currentPaymentPlanId}
                            selectedPaymentPlanId={values.paymentPlanId}
                            selectPaymentPlan={(id) => {
                              setFieldValue('paymentPlanId', id);
                              setTimeout(() => validateForm(), 0);
                            }}
                          />
                          {errors?.paymentPlanId && <Box color="danger">{errors.paymentPlanId as string}</Box>}
                        </Box>
                        {values.paymentPlanId && currentPaymentPlanId && (
                          <Box childGap="xl">
                            <Box>
                              <PaymentPlanChangeImpactedAccounts
                                formValues={values}
                                currentPaymentPlanId={currentPaymentPlanId}
                                alias={organizationAlias}
                                paymentPlans={paymentPlans}
                                isSubmitting={isSubmitting}
                                setTotalImpactedAccounts={setTotalImpactedAccounts}
                              />
                            </Box>
                            <Box>
                              <DateInput
                                label="Proposal Effective Date"
                                name="effectiveDate"
                                id="effectiveDate"
                                placeholder={'Pick a date'}
                                dateFormat={DATE_FORMAT}
                                isDisabled={isSubmitting}
                                width="2xl"
                                value={values.effectiveDate}
                                isRequired
                                error={
                                  touched.effectiveDate && errors.effectiveDate ? errors.effectiveDate : undefined
                                }
                                datePickerProps={{
                                  minDate: DateTime.now().plus({ days: 1 }).toJSDate(),
                                  onChange: (date) => {
                                    if (date && !Array.isArray(date) && date instanceof Date) {
                                      return setFieldValue(
                                        'effectiveDate',
                                        DateTime.fromJSDate(date).toLocal().toFormat(DATE_FORMAT),
                                      );
                                    }
                                  },
                                  openToDate: new Date(),
                                }}
                                textInputProps={{
                                  onClear: () => setFieldValue('effectiveDate', ''),
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box>
                <Field
                  title={`Should ${organizationAlias} set up their own bank account to receive project payments?`}
                  name="setUpBankAccount"
                  id="setUpBankAccount"
                  options={setUpBankAccountOptions}
                  component={FormikRadioGroup}
                  isRequired
                  isDisabled={isSubmitting}
                />
                {errors?.setUpBankAccount && <Box color="danger">{errors.setUpBankAccount as string}</Box>}
              </Box>
              {!hideLegalSection && (
                <>
                  <Box>
                    <Box>
                      <Field
                        title={`Does ${organizationAlias} represent its own legal entity?`}
                        name="legalEntity"
                        id="legalEntity"
                        options={legalEntityOptions}
                        component={FormikRadioGroup}
                        isRequired
                        isDisabled={isSubmitting}
                      />
                    </Box>
                    {errors?.legalEntity && <Box color="danger">{errors.legalEntity as string}</Box>}
                  </Box>
                </>
              )}
            </Box>

            <Box
              direction={{
                base: 'column',
                tablet: 'row',
              }}
              alignItems={{
                base: 'stretch',
                tablet: 'flex-end',
              }}
              justifyContent={{
                tablet: 'flex-end',
              }}
              childGap="sm"
              style={{ flexShrink: 0 }}
              padding="lg 0 0 0"
            >
              <Button
                variant="secondary"
                tone="neutral"
                size="md"
                isDisabled={isLoadingData}
                isLoading={isSubmitting}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button variant="primary" size="md" type="submit" isDisabled={isLoading} isLoading={isSubmitting}>
                {values.shouldHavePaymentSchedule === 'yes' && currentPaymentPlanId ? 'Send Proposal' : 'Save'}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};
